/**
 * components/riskTerminal/MarginFooter.jsx — upstox-086 (the Claude Design handoff, section 3)
 *
 * The muted 32 px margin footer under the Trading book:
 *   MARGIN ( COLLATERAL ₹X + CASH ₹Y )   AVAILABLE ₹A  [20 steps]  USED ₹U  READOUT
 * (Upstox: PLEDGE instead of COLLATERAL). No account name: the account switch above says it. On a narrow track the bracket
 * stays on line 1 and AVAILABLE · steps · USED · readout wrap together to line 2. States: not signed in, Upstox closed till
 * 05:30 (the last read shown), an amber "MARGIN · Nm old" chip from 5 min (hover: last read hh:mm), an error with the
 * broker's words. Never ₹0 for a figure that is missing.
 */

import React from 'react';
import { C } from './theme';
import { marginView, rupees } from './marginModel';

const CAP9 = { fontSize: '9px', letterSpacing: '0.12em', color: '#6b655c' };
const MUTED_CAP = { fontSize: '9px', letterSpacing: '0.12em', color: '#878176' };
const HATCH = 'repeating-linear-gradient(135deg,#efece6 0 4px,#fff 4px 8px)';
const TONE = { grey: '#585349', amber: C.amber, red: C.red };
const READ_TONE = { grey: '#878176', amber: C.amber, red: C.red };

export function Steps({ gauge, style }) {
  const dots = Array.from({ length: gauge.steps }, (_, i) => {
    const on = gauge.short || i >= gauge.steps - gauge.filled;
    return <span key={i} data-step={on ? 'on' : 'off'} style={{ flex: 1, height: '3px', boxSizing: 'border-box', background: on ? TONE[gauge.tone] : '#e5e1d8' }} />;
  });
  if (gauge.credit) dots.push(<span key="credit" data-step="credit" style={{ flex: 1, height: '3px', boxSizing: 'border-box', border: '1.5px solid #b9b2a4' }} />);
  return <span data-steps style={{ flex: 1, minWidth: '120px', display: 'flex', gap: '2px', alignItems: 'center', ...style }}>{dots}</span>;
}

function Bracket({ v }) {
  const s = v.seg;
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px', whiteSpace: 'nowrap', flex: '0 0 auto' }}>
      {v.old ? (
        <span data-margin-old className="rt-tip" data-tip={'last read ' + v.lastRead} style={{ fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, color: C.amber, background: C.amberTint, border: '1px solid ' + C.amberLine, padding: '1px 6px' }}>
          {'MARGIN · ' + v.ageMin + 'm old'}</span>
      ) : <span style={{ ...CAP9, fontWeight: 600 }}>MARGIN</span>}
      {v.hasBracket && (
        <>
          <span style={{ fontSize: '11px', color: '#878176' }}>(</span>
          <span style={MUTED_CAP}>{v.labels.collateral || 'COLLATERAL'}</span>
          <span data-margin-collateral style={{ fontSize: '11px', color: '#6b655c' }}>{rupees(s.collateral)}</span>
          <span style={{ fontSize: '11px', color: '#878176' }}>+</span>
          <span style={MUTED_CAP}>CASH</span>
          <span data-margin-cash style={{ fontSize: '11px', color: '#6b655c' }}>{rupees(s.cash)}</span>
          <span style={{ fontSize: '11px', color: '#878176' }}>)</span>
        </>
      )}
    </span>
  );
}

function Figures({ seg, gauge, labels }) {
  const avRed = gauge && gauge.short;
  return (
    <span data-margin-group style={{ display: 'flex', alignItems: 'center', gap: '12px', whiteSpace: 'nowrap', flex: '1 1 auto' }}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }} className="rt-tip" data-tip={(labels.available || 'Available') + ' — the broker\'s own figure'}>
        <span style={CAP9}>AVAILABLE</span>
        <span data-margin-available style={{ fontSize: '13px', fontWeight: 600, color: avRed ? C.red : C.ink }}>{rupees(seg.available)}</span>
      </span>
      {gauge && <Steps gauge={gauge} />}
      <span style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
        <span style={CAP9}>USED</span>
        <span data-margin-used style={{ fontSize: '13px', fontWeight: 600, color: C.ink, ...(seg.used != null && seg.used < 0 ? { textDecoration: 'underline dotted #aba396', textUnderlineOffset: '3px' } : {}) }}>{rupees(seg.used)}</span>
      </span>
      {gauge && gauge.readout && <span data-margin-readout style={{ fontSize: '9px', letterSpacing: '0.1em', fontWeight: 600, color: READ_TONE[gauge.readTone] }}>{gauge.readout}</span>}
    </span>
  );
}

const ROW = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 18px', padding: '9px 20px', background: '#f8f7f3', borderTop: '1px solid #efece6', minHeight: '32px', boxSizing: 'border-box' };

export default function MarginFooter({ block, now }) {
  const v = marginView(block, now);
  if (v.state === 'none') return null;
  const label = <span style={{ ...CAP9, fontWeight: 600 }}>MARGIN</span>;
  const lastFigures = v.seg && (v.seg.available != null || v.seg.used != null)
    ? ' · last read ' + v.lastRead + ': AVAILABLE ' + rupees(v.seg.available) + ' · USED ' + rupees(v.seg.used) : '';
  if (v.state === 'pending') {
    return <div data-margin-footer data-margin-state="pending" style={ROW}>{label}<span style={{ fontSize: '11px', color: '#878176' }}>reading…</span></div>;
  }
  if (v.state === 'not_signed_in') {
    const kite = v.broker === 'KITE';
    return (
      <div data-margin-footer data-margin-state="not_signed_in" style={ROW}>
        {label}
        <span style={{ fontSize: '10.5px', letterSpacing: '0.1em', fontWeight: 600, color: '#3f3b35', padding: '2px 6px', border: '1px dashed #878176', backgroundImage: HATCH }}>{kite ? 'NOT SIGNED IN' : 'NOT SIGNED IN'}</span>
        <span style={{ fontSize: '11px', color: '#6b655c' }}>{"margin can't be read · " + (kite ? 'sign in from Settings → Connections' : 'sign in to Upstox')}{lastFigures}</span>
      </div>
    );
  }
  if (v.state === 'unavailable') {
    return (
      <div data-margin-footer data-margin-state="unavailable" style={ROW}>
        {label}
        <span style={{ fontSize: '11px', color: '#878176' }}>{'unavailable till 05:30' + lastFigures}</span>
      </div>
    );
  }
  if (v.state === 'error') {
    return (
      <div data-margin-footer data-margin-state="error" style={ROW}>
        {label}
        <span style={{ fontSize: '10.5px', letterSpacing: '0.1em', fontWeight: 600, color: '#3f3b35', padding: '2px 6px', border: '1px dashed #878176', backgroundImage: HATCH }}>CAN'T BE READ</span>
        <span className="rt-tip" data-tip={v.message} style={{ fontSize: '11px', color: '#6b655c' }}>{(v.message.length > 90 ? v.message.slice(0, 90) + '…' : v.message) + lastFigures}</span>
      </div>
    );
  }
  // ok — with nothing to show if the broker sent no equity figures at all
  if (!v.seg) return <div data-margin-footer data-margin-state="ok" style={ROW}>{label}<span style={{ fontSize: '11px', color: '#878176' }}>no figures in the broker's answer</span></div>;
  return (
    <div data-margin-footer data-margin-state="ok" style={ROW}>
      <Bracket v={v} />
      <Figures seg={v.seg} gauge={v.gauge} labels={v.labels} />
      {v.mcx && (
        <span data-margin-mcx style={{ flex: '1 0 100%', display: 'flex', alignItems: 'center', gap: '12px', whiteSpace: 'nowrap' }}>
          <span style={{ ...CAP9, fontWeight: 600 }}>MCX</span>
          <span style={CAP9}>AVAILABLE</span><span style={{ fontSize: '13px', fontWeight: 600 }}>{rupees(v.mcx.available)}</span>
          <span style={CAP9}>USED</span><span style={{ fontSize: '13px', fontWeight: 600 }}>{rupees(v.mcx.used)}</span>
          {v.mcxGauge && v.mcxGauge.readout && <span style={{ fontSize: '9px', letterSpacing: '0.1em', fontWeight: 600, color: READ_TONE[v.mcxGauge.readTone] }}>{v.mcxGauge.readout}</span>}
        </span>
      )}
    </div>
  );
}
